import { DEFAULT_OWLBOT_MODEL } from '@readme/backend/iso';

import { createBoundedUseStore, actionLog } from '../util';
import { createCrossFrameStore } from '../util/createCrossFrameStore';

// custom tone
export enum Tone {
  Custom = 'custom',
  Neutral = 'neutral',
  Professional = 'professional',
  Whimsical = 'whimsical',
}

// length in words
export enum AnswerLength {
  Long = 'long',
  Medium = 'medium',
  Short = 'short',
  Unrestricted = 'unrestricted',
}

export interface Model {
  modelId: string;
}

export interface OwlbotStoreState {
  answerLength: AnswerLength;
  customTone: string | null;
  defaultAnswer: string;
  exampleQuestions: {
    question1: string;
    question2: string;
    question3: string;
  };
  forbiddenWords: string;
  knowledge: string;
  projectScopedKnowledge: boolean;
  selectedModel: Model;
  showAiDisclaimer: boolean;
  tone: Tone;
}

export interface OwlbotStoreActions {
  reset: (resetData?: OwlbotStoreState) => void;
  updateAnswerLength: (length: AnswerLength) => void;
  updateCustomTone: (tone: string) => void;
  updateDefaultAnswer: (answer: string) => void;
  updateExampleQuestions: (questions: { question1: string; question2: string; question3: string }) => void;
  updateForbiddenWords: (words: string) => void;
  updateKnowledge: (knowledge: string) => void;
  updateProjectScopedKnowledge: (enabled: boolean) => void;
  updateSelectedModel: (model: Model) => void;
  updateShowAiDisclaimer: (enabled: boolean) => void;
  updateTone: (tone: Tone) => void;
}

type OwlbotStore = OwlbotStoreActions & OwlbotStoreState;

const initialState: OwlbotStoreState = {
  answerLength: AnswerLength.Unrestricted,
  customTone: null,
  defaultAnswer: '',
  exampleQuestions: {
    question1: '',
    question2: '',
    question3: '',
  },
  forbiddenWords: '',
  knowledge: '',
  projectScopedKnowledge: false,
  selectedModel: { modelId: DEFAULT_OWLBOT_MODEL },
  showAiDisclaimer: false,
  tone: Tone.Neutral,
};

export const owlbotStore = createCrossFrameStore<OwlbotStore>('OwlbotStore', set => {
  let resetState = initialState;

  return {
    ...initialState,

    updateAnswerLength: length => {
      set(
        state => {
          state.answerLength = length;
        },
        false,
        actionLog('updateAnswerLength', length),
      );
    },

    updateDefaultAnswer: answer => {
      set(
        state => {
          state.defaultAnswer = answer;
        },
        false,
        actionLog('updateDefaultAnswer', answer),
      );
    },

    updateCustomTone: customTone => {
      set(
        state => {
          state.customTone = customTone;
        },
        false,
        actionLog('updateCustomTone', customTone),
      );
    },

    updateExampleQuestions: questions => {
      set(
        state => {
          state.exampleQuestions = questions;
        },
        false,
        actionLog('updateExampleQuestions', questions),
      );
    },

    updateForbiddenWords: words => {
      set(
        state => {
          state.forbiddenWords = words;
        },
        false,
        actionLog('updateForbiddenWords', words),
      );
    },

    updateKnowledge: knowledge => {
      set(
        state => {
          state.knowledge = knowledge;
        },
        false,
        actionLog('updateKnowledge', knowledge),
      );
    },

    updateSelectedModel: model => {
      set(
        state => {
          state.selectedModel = model;
        },
        false,
        actionLog('updateSelectedModel', model),
      );
    },

    updateProjectScopedKnowledge: enabled => {
      set(
        state => {
          state.projectScopedKnowledge = enabled;
        },
        false,
        actionLog('updateProjectScopedKnowledge', enabled),
      );
    },

    updateShowAiDisclaimer: enabled => {
      set(
        state => {
          state.showAiDisclaimer = enabled;
        },
        false,
        actionLog('updateShowAiDisclaimer', enabled),
      );
    },

    updateTone: tone => {
      set(
        state => {
          state.tone = tone;
        },
        false,
        actionLog('updateTone', tone),
      );
    },

    reset: (resetData?: OwlbotStoreState) => {
      resetState = resetData ?? initialState;
      set(
        state => {
          state.answerLength = resetState.answerLength;
          state.defaultAnswer = resetState.defaultAnswer;
          state.exampleQuestions = resetState.exampleQuestions;
          state.forbiddenWords = resetState.forbiddenWords;
          state.knowledge = resetState.knowledge;
          state.selectedModel = resetState.selectedModel;
          state.tone = resetState.tone;
          state.customTone = resetState.customTone;
          state.projectScopedKnowledge = resetState.projectScopedKnowledge;
          state.showAiDisclaimer = resetState.showAiDisclaimer;
        },
        false,
        actionLog('reset'),
      );
    },
  };
});

export const useOwlbotStore = createBoundedUseStore(owlbotStore);
